/**
 * MFALoginFlow — post-sign-in TOTP/SMS challenge UI.
 *
 * UI preserves the original legacy layout (lock icon, factor picker,
 * trust-device checkbox, success confirmation). Under the hood:
 *
 *   - single verify path via `useVerifyTotpMutation` (RTK) — no more
 *     triple-retry loops that fired multiple challenges on every attempt
 *   - "Remember device" now calls /functions/v1/trust-issue (F-024 v2)
 *     which mints an HttpOnly cookie + writes a trusted_devices row; on
 *     subsequent sign-ins the trust-redeem function pre-positions a
 *     redemption that the access-token hook consumes to stamp aal=aal2
 *     directly into the JWT
 *   - `onComplete(true)` always fires after a successful verify so the
 *     parent gate can transition out of MFA_REQUIRED
 *   - uses the shared `useListMfaFactorsQuery` to avoid a second factor
 *     fetch (MFAGuard already called it)
 */
import React, { useEffect, useState } from "react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "../../../lib/supabase";
import {
  useIssueMfaChallengeMutation,
  useListMfaFactorsQuery,
  useVerifyMfaWithChallengeMutation,
  useVerifyTotpMutation,
} from "../api/authApi";
import { useAuth } from "../context/AuthContext";
import type { MfaFactor } from "../types/authTypes";

interface MFALoginFlowProps {
  user: User | null | undefined;
  onComplete: (success: boolean) => void;
}

function getDefaultDeviceName(): string {
  try {
    const ua = navigator.userAgent || "";
    const browser = ua.includes("Edg")
      ? "Edge"
      : ua.includes("OPR") || ua.includes("Opera")
        ? "Opera"
        : ua.includes("Firefox")
          ? "Firefox"
          : ua.includes("Chrome") && !ua.includes("Chromium")
            ? "Chrome"
            : ua.includes("Safari")
              ? "Safari"
              : "Browser";
    const platform = navigator.platform || "Desktop";
    return `${browser} on ${platform}`;
  } catch {
    return "Trusted Device";
  }
}

/**
 * F-024 v2: issue a server-side trusted-device row + HttpOnly cookie via the
 * trust-issue edge function. The function requires an AAL2 JWT — call this
 * IMMEDIATELY after a successful verifyTotp(), while the access token is
 * fresh. On success the browser receives Set-Cookie: sb_trust=...; HttpOnly;
 * Secure; SameSite=Strict; subsequent sign-ins automatically forward it to
 * /functions/v1/trust-redeem which positions a redemption row for the hook.
 */
async function rememberDevice(deviceName: string): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) return;

  const supabaseUrl = (import.meta as unknown as { env?: { VITE_SUPABASE_URL?: string } })
    .env?.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    console.warn("[MFALoginFlow] VITE_SUPABASE_URL missing — skipping trust-issue");
    return;
  }

  const resp = await fetch(`${supabaseUrl}/functions/v1/trust-issue`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ label: deviceName }),
  });
  if (!resp.ok) {
    console.warn("[MFALoginFlow] trust-issue failed", resp.status);
  }
}

export function MFALoginFlow({ user, onComplete }: MFALoginFlowProps) {
  const { userProfile } = useAuth();
  const factorsQuery = useListMfaFactorsQuery(undefined, { skip: !user });
  const [verifyTotp, verifyState] = useVerifyTotpMutation();
  const [issueChallenge, challengeState] = useIssueMfaChallengeMutation();
  const [verifyWithChallenge, verifySmsState] = useVerifyMfaWithChallengeMutation();

  const [selectedFactor, setSelectedFactor] = useState<MfaFactor | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [deviceName, setDeviceName] = useState("");
  // SMS-only: the challenge id issued when the user picked a phone
  // factor. Verifying against this exact challenge avoids a second
  // Twilio SMS and the "user typed code, then we send the SMS too late"
  // race. Reset when the user goes back to the picker.
  const [smsChallengeId, setSmsChallengeId] = useState<string | null>(null);
  const [smsResendCooldown, setSmsResendCooldown] = useState(0);

  const verifiedFactors = [
    ...(factorsQuery.data?.totp ?? []),
    ...(factorsQuery.data?.phone ?? []),
  ].filter((f) => f.status === "verified");

  // When there's exactly one verified factor, skip the picker entirely.
  useEffect(() => {
    if (!selectedFactor && verifiedFactors.length === 1) {
      setSelectedFactor(verifiedFactors[0]);
    }
  }, [selectedFactor, verifiedFactors]);

  const isTotp = selectedFactor?.factor_type === "totp";
  const minOtpLen = isTotp ? 6 : 3;
  const maxOtpLen = isTotp ? 6 : 10;

  // When the user picks a phone factor, fire `mfa.challenge` upfront —
  // GoTrue treats `challenge` as the SMS-send trigger for phone
  // factors. Without this the user sees the code-entry box but no SMS
  // ever arrives. TOTP is offline, no challenge needed here (verifyTotp
  // bundles challenge + verify in one shot).
  useEffect(() => {
    if (!selectedFactor) return;
    if (selectedFactor.factor_type !== "phone") return;
    if (smsChallengeId) return; // already issued for this factor
    let cancelled = false;
    void (async () => {
      try {
        const { challengeId } = await issueChallenge({
          factorId: selectedFactor.id,
        }).unwrap();
        if (cancelled) return;
        setSmsChallengeId(challengeId);
        // 30s cooldown to mirror Twilio's resend window and the
        // EditProfile rate-limit message ("Bitte warten Sie 60 Sekunden")
        setSmsResendCooldown(30);
      } catch (err) {
        if (cancelled) return;
        const msg = ((err as { message?: string })?.message ?? "").toLowerCase();
        if (msg.includes("rate") || msg.includes("59 seconds") || msg.includes("60 seconds")) {
          setError("Zu viele SMS-Anfragen. Bitte warten Sie 60 Sekunden.");
        } else {
          setError(
            `SMS konnte nicht versendet werden: ${
              (err as { message?: string })?.message ?? "Unbekannter Fehler"
            }`,
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedFactor, smsChallengeId, issueChallenge]);

  // Cooldown ticker for the "SMS erneut senden" link.
  useEffect(() => {
    if (smsResendCooldown <= 0) return;
    const t = window.setTimeout(() => {
      setSmsResendCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearTimeout(t);
  }, [smsResendCooldown]);

  const resendSms = async () => {
    if (!selectedFactor || selectedFactor.factor_type !== "phone") return;
    if (smsResendCooldown > 0) return;
    setError("");
    try {
      const { challengeId } = await issueChallenge({
        factorId: selectedFactor.id,
      }).unwrap();
      setSmsChallengeId(challengeId);
      setSmsResendCooldown(30);
    } catch (err) {
      const msg = ((err as { message?: string })?.message ?? "").toLowerCase();
      if (msg.includes("rate") || msg.includes("seconds")) {
        setError("Zu viele SMS-Anfragen. Bitte warten Sie 60 Sekunden.");
      } else {
        setError(
          `SMS konnte nicht versendet werden: ${
            (err as { message?: string })?.message ?? "Unbekannter Fehler"
          }`,
        );
      }
    }
  };

  // Reset SMS state when the user goes back to the factor picker.
  const handleBackToPicker = () => {
    setSelectedFactor(null);
    setOtpCode("");
    setError("");
    setSmsChallengeId(null);
    setSmsResendCooldown(0);
  };

  // Super admins get an Abbrechen button so they can still reach admin
  // tools if their authenticator is unavailable. RLS enforces anything
  // sensitive server-side.
  const roleName = (userProfile?.role ?? "").toLowerCase();
  const subRoles = (userProfile?.sub_roles ?? []).map((r) => r.toLowerCase());
  const isSuperAdmin =
    roleName === "super admin" ||
    roleName === "superadmin" ||
    subRoles.includes("super admin") ||
    subRoles.includes("superadmin");

  const handleVerify = async () => {
    if (!selectedFactor || otpCode.length < minOtpLen) {
      setError("Bitte wählen Sie einen Faktor und geben Sie den Code ein.");
      return;
    }
    setError("");
    try {
      if (selectedFactor.factor_type === "phone") {
        // SMS: the challenge was issued upfront in the useEffect above so
        // the Twilio message has already been sent. Verify against THAT
        // challenge — don't go through verifyTotp which would issue a
        // brand-new challenge (sending a second SMS) at this instant.
        if (!smsChallengeId) {
          setError(
            "Kein SMS-Challenge aktiv. Bitte fordern Sie den Code erneut an.",
          );
          return;
        }
        await verifyWithChallenge({
          factorId: selectedFactor.id,
          challengeId: smsChallengeId,
          code: otpCode,
        }).unwrap();
      } else {
        await verifyTotp({
          factorId: selectedFactor.id,
          code: otpCode,
        }).unwrap();
      }

      if (trustDevice) {
        const name = deviceName.trim() || getDefaultDeviceName();
        try {
          await rememberDevice(name);
        } catch (e) {
          console.warn("[MFALoginFlow] rememberDevice failed", e);
        }
      }

      setSuccess(true);
      // Small delay so the user sees the success confirmation before the
      // popup closes.
      window.setTimeout(() => onComplete(true), 600);
    } catch (err) {
      const e = err as { message?: string };
      const msg = (e?.message ?? "").toLowerCase();
      if (msg.includes("invalid") || msg.includes("code")) {
        setError(
          "Ungültiger Code. Stellen Sie sicher, dass:\n• Die Uhrzeit auf Ihrem Gerät korrekt ist\n• Sie den aktuellsten Code verwenden\n• Die Authenticator-App korrekt eingerichtet wurde",
        );
      } else if (msg.includes("expired")) {
        setError("Der Code ist abgelaufen. Generieren Sie einen neuen Code.");
      } else {
        setError(
          `Verifizierungsfehler: ${e?.message ?? "Unbekannter Fehler"}`,
        );
      }
    }
  };

  if (factorsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Lade Zwei-Faktor-Authentifizierung...</p>
        </div>
      </div>
    );
  }

  if (verifiedFactors.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">2FA nicht verfügbar</h3>
            <p className="text-gray-600 mb-4">
              Keine verifizierten 2FA-Faktoren gefunden. Bitte richten Sie zuerst 2FA ein.
            </p>
            <button
              onClick={() => onComplete(false)}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
            >
              Zurück zur App
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        <div className="text-center mb-6">
          <div className="text-blue-600 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Zwei-Faktor-Authentifizierung</h2>
          <p className="text-gray-600">Bitte verifizieren Sie Ihre Identität</p>
          <p className="text-sm text-blue-600 mt-2">
            🔒 Diese Verifizierung ist erforderlich, um Ihre Sitzung zu sichern
          </p>
        </div>

        {!success && !selectedFactor && (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {verifiedFactors.length > 1 ? "Wählen Sie eine 2FA-Methode:" : "Verfügbare 2FA-Methode:"}
            </h3>
            <div className="space-y-3">
              {verifiedFactors.map((factor) => (
                <button
                  key={factor.id}
                  onClick={() => setSelectedFactor(factor)}
                  className="w-full p-3 border border-gray-300 rounded-md text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">
                        {factor.factor_type === "totp" ? "Authenticator-App" : "SMS"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {factor.factor_type === "totp"
                          ? "Code aus Authenticator-App verwenden"
                          : "Code per SMS erhalten"}
                      </div>
                    </div>
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {!success && selectedFactor && (
          <div className="space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {selectedFactor.factor_type === "totp" ? "Authenticator-App" : "SMS"} Code eingeben
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {selectedFactor.factor_type === "totp"
                  ? "Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein"
                  : "Geben Sie den Code ein, der Ihnen per SMS gesendet wurde"}
              </p>
            </div>

            <div>
              <input
                type="text"
                value={otpCode}
                onChange={(e) => {
                  const filtered = e.target.value.replace(/\D/g, "").slice(0, maxOtpLen);
                  setOtpCode(filtered);
                }}
                onKeyDown={(e) => {
                  const phoneNeedsChallenge =
                    selectedFactor?.factor_type === "phone" && !smsChallengeId;
                  if (
                    e.key === "Enter" &&
                    otpCode.length >= minOtpLen &&
                    !verifyState.isLoading &&
                    !verifySmsState.isLoading &&
                    !phoneNeedsChallenge
                  ) {
                    e.preventDefault();
                    void handleVerify();
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, maxOtpLen);
                  setOtpCode(pasted);
                }}
                placeholder={isTotp ? "000000" : "000"}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-lg tracking-widest"
                maxLength={maxOtpLen}
                autoFocus
                inputMode="numeric"
                pattern="[0-9]*"
              />
              <p className="text-xs text-gray-500 text-center mt-1">
                Drücken Sie Enter zum Bestätigen. TOTP: 6-stellig, SMS: mindestens 3-stellig
              </p>
              {selectedFactor.factor_type === "phone" && (
                <div className="text-xs text-center mt-2">
                  {challengeState.isLoading && !smsChallengeId ? (
                    <span className="text-gray-500">SMS wird gesendet…</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void resendSms()}
                      disabled={smsResendCooldown > 0 || challengeState.isLoading}
                      className="text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed underline"
                    >
                      {smsResendCooldown > 0
                        ? `SMS erneut senden (${smsResendCooldown}s)`
                        : "SMS erneut senden"}
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="trustDevice"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="trustDevice" className="text-sm text-gray-700">
                  Dieses Gerät für 30 Tage vertrauen
                </label>
              </div>
              {trustDevice && (
                <input
                  type="text"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  placeholder={getDefaultDeviceName()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
              <p className="text-xs text-gray-500">
                Wenn aktiviert, müssen Sie auf diesem Gerät 30 Tage lang keine 2FA eingeben
              </p>
            </div>

            {error && (
              <div className="text-red-600 text-sm text-center whitespace-pre-line">
                {error}
              </div>
            )}

            <div className="flex space-x-3">
              {verifiedFactors.length > 1 && (
                <button
                  onClick={handleBackToPicker}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 transition-colors"
                  disabled={verifyState.isLoading || verifySmsState.isLoading}
                >
                  Zurück
                </button>
              )}
              <button
                onClick={() => void handleVerify()}
                disabled={
                  verifyState.isLoading ||
                  verifySmsState.isLoading ||
                  otpCode.length < minOtpLen ||
                  // For SMS, block submit until the challenge is back —
                  // otherwise we'd fall through the "no challenge active"
                  // error path on a too-eager click.
                  (selectedFactor?.factor_type === "phone" && !smsChallengeId)
                }
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {verifyState.isLoading || verifySmsState.isLoading
                  ? "Überprüfe..."
                  : "Verifizieren"}
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-center mb-2">
              <svg className="w-8 h-8 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-semibold text-green-800">
                Verifizierung erfolgreich!
              </h3>
            </div>
            <p className="text-sm text-green-700 mb-3">
              Weiterleitung erfolgt sofort...
            </p>
            <button
              onClick={() => onComplete(true)}
              className="text-xs text-blue-600 hover:text-blue-800 underline"
            >
              Hier klicken, falls die Weiterleitung nicht automatisch erfolgt
            </button>
          </div>
        )}

        {!success && (
          <div className="mt-6 flex flex-col items-center gap-2">
            <button
              onClick={async () => {
                try {
                  await supabase.auth.signOut();
                } finally {
                  window.location.href = "/login";
                }
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Abmelden
            </button>
            {isSuperAdmin && (
              <button
                onClick={() => onComplete(false)}
                className="text-xs text-gray-400 hover:text-gray-600"
                title="Superadmin-Diagnose: ohne AAL2 in die App, RLS bleibt aktiv"
              >
                Abbrechen (Superadmin-Diagnose)
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
